import type { LoginFormData, SignUpFormData, AuthResponse, User, SessionInfo } from '@/types/auth';
import type { IAuthService } from './IAuthService';

/**
 * Mock implementation of the authentication service for testing
 * Provides realistic test responses without external dependencies
 */
export class MockAuthService implements IAuthService {
  private mockUsers: Map<string, User> = new Map();
  private currentUser: User | null = null;
  private sessionExists: boolean = false;

  constructor() {
    // Pre-populate with test users
    this.mockUsers.set('test@example.com', {
      id: 'mock-user-1',
      email: 'test@example.com',
      timeJoined: Date.now() - 86400000, // 1 day ago
      tenantIds: ['public']
    });

    this.mockUsers.set('admin@example.com', {
      id: 'mock-user-2',
      email: 'admin@example.com',
      timeJoined: Date.now() - 604800000, // 1 week ago
      tenantIds: ['public', 'admin']
    });
  }

  /**
   * Sign in user with email and password
   */
  async signIn(formData: LoginFormData): Promise<AuthResponse> {
    // Simulate network delay
    await this.delay(500);

    // Validate email format
    if (!this.validateEmail(formData.email)) {
      return {
        status: 'FIELD_ERROR',
        formFields: [{ id: 'email', error: 'Invalid email format' }]
      };
    }

    // Check for test credentials
    if (formData.email === 'test@example.com' && formData.password === 'password123') {
      const user = this.mockUsers.get(formData.email)!;
      this.currentUser = user;
      this.sessionExists = true;
      return {
        status: 'OK',
        user
      };
    }

    if (formData.email === 'admin@example.com' && formData.password === 'admin123') {
      const user = this.mockUsers.get(formData.email)!;
      this.currentUser = user;
      this.sessionExists = true;
      return {
        status: 'OK',
        user
      };
    }

    // Wrong credentials
    return {
      status: 'WRONG_CREDENTIALS_ERROR'
    };
  }

  /**
   * Sign up user with email and password
   */
  async signUp(formData: SignUpFormData): Promise<AuthResponse> {
    // Simulate network delay
    await this.delay(800);

    // Validate email format
    if (!this.validateEmail(formData.email)) {
      return {
        status: 'FIELD_ERROR',
        formFields: [{ id: 'email', error: 'Invalid email format' }]
      };
    }

    // Validate password
    const passwordValidation = this.validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      return {
        status: 'FIELD_ERROR',
        formFields: [{ id: 'password', error: passwordValidation.message || 'Invalid password' }]
      };
    }

    // Check if user already exists
    if (this.mockUsers.has(formData.email)) {
      return {
        status: 'FIELD_ERROR',
        formFields: [{ id: 'email', error: 'Email already exists' }]
      };
    }

    // Create new user
    const newUser: User = {
      id: `mock-user-${Date.now()}`,
      email: formData.email,
      timeJoined: Date.now(),
      tenantIds: ['public']
    };

    this.mockUsers.set(formData.email, newUser);
    this.currentUser = newUser;
    this.sessionExists = true;

    return {
      status: 'OK',
      user: newUser
    };
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    // Simulate network delay
    await this.delay(200);

    this.currentUser = null;
    this.sessionExists = false;
  }

  /**
   * Check if user session exists and is valid
   */
  async doesSessionExist(): Promise<boolean> {
    // Simulate network delay
    await this.delay(100);
    return this.sessionExists;
  }

  /**
   * Get current user information from session
   */
  async getUserInfo(): Promise<User | null> {
    // Simulate network delay
    await this.delay(200);

    if (!this.sessionExists) {
      return null;
    }

    return this.currentUser;
  }

  /**
   * Get session information
   */
  async getSessionInfo(): Promise<SessionInfo | null> {
    // Simulate network delay
    await this.delay(200);

    if (!this.sessionExists || !this.currentUser) {
      return null;
    }

    return {
      userId: this.currentUser.id,
      accessTokenPayload: {
        email: this.currentUser.email,
        timeJoined: this.currentUser.timeJoined,
        tenantIds: this.currentUser.tenantIds
      },
      sessionHandle: `mock-session-${this.currentUser.id}`
    };
  }

  /**
   * Refresh the current session
   */
  async refreshSession(): Promise<boolean> {
    // Simulate network delay
    await this.delay(300);

    // In mock, always succeed if session exists
    return this.sessionExists;
  }

  /**
   * Check if session needs refresh and attempt refresh if needed
   */
  async ensureValidSession(): Promise<boolean> {
    // Simulate network delay
    await this.delay(150);

    if (!this.sessionExists) {
      return false;
    }

    // In mock, session is always valid if it exists
    return true;
  }

  /**
   * Get user ID from current session
   */
  async getCurrentUserId(): Promise<string | null> {
    // Simulate network delay
    await this.delay(100);

    if (!this.sessionExists || !this.currentUser) {
      return null;
    }

    return this.currentUser.id;
  }

  /**
   * Check if user is authenticated with valid session
   */
  async isAuthenticated(): Promise<boolean> {
    return await this.ensureValidSession();
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): { isValid: boolean; message?: string } {
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' };
    }

    if (!/(?=.*[a-z])/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' };
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' };
    }

    if (!/(?=.*\d)/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one number' };
    }

    return { isValid: true };
  }

  /**
   * Send password reset email to user
   */
  async sendPasswordResetEmail(email: string): Promise<{ status: 'OK' | 'EMAIL_DOES_NOT_EXIST_ERROR' }> {
    // Simulate network delay
    await this.delay(1000);

    // Validate email format
    if (!this.validateEmail(email)) {
      return { status: 'EMAIL_DOES_NOT_EXIST_ERROR' };
    }

    // Check if user exists in mock users
    if (this.mockUsers.has(email)) {
      return { status: 'OK' };
    }

    // Email not found
    return { status: 'EMAIL_DOES_NOT_EXIST_ERROR' };
  }

  /**
   * Submit new password with reset token
   */
  async submitNewPassword(token: string, newPassword: string): Promise<{ status: 'OK' | 'RESET_PASSWORD_INVALID_TOKEN_ERROR' | 'FIELD_ERROR'; formFields?: Array<{ id: string; error: string }> }> {
    // Simulate network delay
    await this.delay(800);

    // Simulate invalid token scenarios
    if (token === 'invalid-token' || token === 'expired-token') {
      return { status: 'RESET_PASSWORD_INVALID_TOKEN_ERROR' };
    }

    // Validate password
    const passwordValidation = this.validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      return {
        status: 'FIELD_ERROR',
        formFields: [{ id: 'password', error: passwordValidation.message || 'Invalid password' }]
      };
    }

    // Mock success - in a real implementation, this would update the user's password
    // For testing purposes, we assume any valid token works
    if (token.startsWith('valid-token-')) {
      return { status: 'OK' };
    }

    // Default to invalid token for any other cases
    return { status: 'RESET_PASSWORD_INVALID_TOKEN_ERROR' };
  }

  /**
   * Utility method to simulate network delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Test utility methods for controlling mock state
   */

  /**
   * Reset mock state (useful for tests)
   */
  reset(): void {
    this.currentUser = null;
    this.sessionExists = false;
    this.mockUsers.clear();

    // Re-populate with default test users
    this.mockUsers.set('test@example.com', {
      id: 'mock-user-1',
      email: 'test@example.com',
      timeJoined: Date.now() - 86400000,
      tenantIds: ['public']
    });

    this.mockUsers.set('admin@example.com', {
      id: 'mock-user-2',
      email: 'admin@example.com',
      timeJoined: Date.now() - 604800000,
      tenantIds: ['public', 'admin']
    });
  }

  /**
   * Set current user for testing (bypasses authentication)
   */
  setCurrentUser(user: User | null): void {
    this.currentUser = user;
    this.sessionExists = user !== null;
  }

  /**
   * Add a mock user for testing
   */
  addMockUser(email: string, user: User): void {
    this.mockUsers.set(email, user);
  }
}